'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { Data } from 'effect';
import type { UIMessage } from 'ai';
import { pickLatestSearchToolPart, pickSearchToolPartForMessage } from './client/ActivityAiResultsBridge';
import type { SearchActivitiesFilters } from '@funkey/ai-tools/schemas';

/** Per-activity AI relevancy/popularity scores from the `search_activities` tool output. */
export type AiActivityScore = {
  similarity: number;
  rankScore: number;
  popularityBoost: number;
};

/**
 * One ranked AI result: the activity the AI matched plus its group (ADR 0007).
 * Hydration keys off `groupId` to resolve the group's PRIMARY representative live
 * against the catalogue (region-eligible, filter-respecting), rather than showing
 * the matched member. `groupId` is `null` for ungrouped/FAQ entries, which
 * hydrate directly.
 */
export type AiActivityEntry = {
  activityId: number;
  groupId: number | null;
};

/**
 * The structured filters the AI turn applied, read straight off the
 * `search_activities` tool output (the same echoed object the URL bridge
 * consumes). Fed synchronously into the grid's `getAiResults` query so the
 * server budget recheck fires and the order matches the turn — WITHOUT waiting
 * for the throttled URL side-channel to catch up (FUN-4558). `maxBudget` is the
 * displayed VAT-incl value; the server converts incl→excl, so pass it through
 * unchanged.
 */
export type AiTurnFilters = {
  maxBudget: number | null;
  minBudget: number | null;
  participants: number | null;
  sort: 'price.ASC' | 'price.DESC' | 'popularity.DESC' | 'distance.ASC' | null;
  locationType: ('INDOOR' | 'OUTDOOR' | 'VIRTUAL')[] | null;
};

/**
 * `ActivityAi_SearchTurn` — the single derived model for "which AI result set
 * does the grid render right now" (ADR 0003 / 0007). Read straight off the
 * persisted `chat.messages` — no caching layer, so reopening a chat re-derives
 * the identical set. `entries` is the FAQ union (search ∪ opted-in FAQ), each
 * paired with its group; hydration resolves each group's primary representative
 * live by group id (ADR 0007), so the visible set — and its count — is settled at
 * hydration against the current filters, not frozen in the message. `ids` is the
 * matched-member id list (a convenience projection of `entries`) still used for
 * loading/analytics and the admin score lookup.
 */
export type ActivityAi_SearchTurn = Data.TaggedEnum<{
  /** The selected turn's tool call hasn't produced output yet. */
  Pending: {};
  /** A finished turn — `entries` (group-keyed) drives the grid, `filters`
   * parameterizes the grid query synchronously, `scores` feeds the admin badge. */
  Cached: {
    ids: number[];
    entries: AiActivityEntry[];
    filters: AiTurnFilters;
    scores: Record<number, AiActivityScore>;
  };
  /** The selected turn's tool call errored. */
  Failed: {};
  /** No turn in the thread has called `search_activities`/`retrieve_faqs` yet. */
  NoSearch: {};
}>;
export const ActivityAi_SearchTurn = Data.taggedEnum<ActivityAi_SearchTurn>();

/**
 * Sentinel `selectedMessageId` meaning "dismiss the AI hit set" (e.g. the
 * filter drawer's "Change search" action) — forces `NoSearch` regardless of
 * `messages`, so the grid falls back to a plain filtered search. A real new
 * turn's auto-write always resets `resultMessageId` back to `null` (the
 * default), un-dismissing it.
 */
export const DISMISSED_SEARCH_TURN = '__dismissed__';
function scoresById(scores: ReadonlyArray<{
  id: number;
} & AiActivityScore>): Record<number, AiActivityScore> {
  const map: Record<number, AiActivityScore> = {};
  for (const s of scores) {
    map[s.id] = {
      similarity: s.similarity,
      rankScore: s.rankScore,
      popularityBoost: s.popularityBoost
    };
  }
  return map;
}

/** Empty filter set — a FAQ-only turn (no `search_activities` call) applies none. */
const NO_TURN_FILTERS: AiTurnFilters = {
  maxBudget: null,
  minBudget: null,
  participants: null,
  sort: null,
  locationType: null
};

/** Project the AI-echoed search filters onto the subset the grid query needs. */
function toTurnFilters(filters: SearchActivitiesFilters | null): AiTurnFilters {
  if (!filters) return NO_TURN_FILTERS;
  return {
    maxBudget: filters.maxBudget ?? null,
    minBudget: filters.minBudget ?? null,
    participants: filters.participants ?? null,
    sort: filters.sort ?? null,
    locationType: filters.locationType?.length ? filters.locationType : null
  };
}

/**
 * Pure derivation — the selected search turn straight from persisted
 * `UIMessage[]`. Defaults to the latest turn with results; pass
 * `selectedMessageId` to pin to a past turn ("go back" navigation). Falls
 * back to the default when that message carries no search/FAQ result.
 */
export function deriveSelectedSearchTurn(messages: ReadonlyArray<UIMessage>, selectedMessageId?: string | null): ActivityAi_SearchTurn {
  if (selectedMessageId === DISMISSED_SEARCH_TURN) return ActivityAi_SearchTurn.NoSearch();
  const picked = (selectedMessageId ? pickSearchToolPartForMessage(messages, selectedMessageId) : null) ?? pickLatestSearchToolPart(messages);
  if (!picked) return ActivityAi_SearchTurn.NoSearch();
  const {
    search,
    faq
  } = picked;

  // FAQ ids feed the grid ONLY when the model opted in via `surfaceInGrid`.
  // FAQ entries carry no group — they hydrate directly (ADR 0007).
  const faqIds = faq && faq.state === 'output-available' && faq.surfaceInGrid ? faq.activityIds : [];
  const faqEntries: AiActivityEntry[] = faqIds.map(id => ({
    activityId: id,
    groupId: null
  }));
  if (search) {
    if (search.state === 'output-error') return ActivityAi_SearchTurn.Failed();
    if (search.state !== 'output-available') return ActivityAi_SearchTurn.Pending();
    const entries = dedupeEntries([...search.activityGroups, ...faqEntries]);
    return ActivityAi_SearchTurn.Cached({
      ids: entries.map(entry => entry.activityId),
      entries,
      filters: toTurnFilters(search.filters),
      scores: scoresById(search.scores)
    });
  }
  if (faqEntries.length === 0) return ActivityAi_SearchTurn.NoSearch();
  return ActivityAi_SearchTurn.Cached({
    ids: faqEntries.map(entry => entry.activityId),
    entries: faqEntries,
    filters: NO_TURN_FILTERS,
    scores: {}
  });
}

/** Dedupe entries by `activityId`, preserving order (retrieval is relevance-ordered). */
function dedupeEntries(entries: ReadonlyArray<AiActivityEntry>): AiActivityEntry[] {
  const seen = new Set<number>();
  const result: AiActivityEntry[] = [];
  for (const entry of entries) {
    if (seen.has(entry.activityId)) continue;
    seen.add(entry.activityId);
    result.push(entry);
  }
  return result;
}

/**
 * Collapse superseded assistant retries. Consecutive assistant messages with no
 * user message between them are retries of ONE user turn ("Opnieuw"/regenerate),
 * and only the last is authoritative — every genuine new turn is preceded by a
 * user message. Drops each assistant message immediately followed by another
 * assistant message, keeping the last of each run.
 *
 * Failed retries persist as their own rows (server-generated ids, insert-only),
 * so a chat that recovered after N failures hydrates N dead "search failed"
 * turns on reopen; this cleans them from the render (FUN-4558). A genuinely-last
 * failed turn (no assistant after it) is kept, so its error + retry box still
 * shows.
 */
export function collapseSupersededAssistantTurns(messages: ReadonlyArray<UIMessage>): UIMessage[] {
  return messages.filter((message, index) => {
    if (message.role !== 'assistant') return true;
    return messages[index + 1]?.role !== 'assistant';
  });
}

/** React hook — re-derives whenever `messages` (or the pinned turn) changes. */
export function useSelectedSearchTurn(messages, selectedMessageId) {
  const $ = _c(3);
  let t0;
  if ($[0] !== messages || $[1] !== selectedMessageId) {
    t0 = deriveSelectedSearchTurn(messages, selectedMessageId);
    $[0] = messages;
    $[1] = selectedMessageId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}